/**
 * Sesión en el cliente.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * EL ACCESS TOKEN VIVE EN UNA VARIABLE DE MÓDULO. NO EN `localStorage`.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `localStorage` es legible por cualquier JavaScript de la página. Una sola dependencia
 * comprometida —o un `dangerouslySetInnerHTML` mal puesto— basta para que el token se
 * exfiltre y el atacante actúe como el usuario hasta que caduque.
 *
 * En memoria, un XSS puede USAR el token mientras la pestaña está abierta, pero no puede
 * llevárselo a otro sitio ni sobrevivir a una recarga. Es una diferencia real.
 *
 * ¿Y la persistencia entre recargas? La da el refresh token, que va en una cookie `httpOnly`
 * que el JavaScript NO puede leer. Al arrancar, la web llama a `/auth/refresh`: si la cookie
 * sigue viva, recupera la sesión sin que el usuario vuelva a escribir nada.
 */
import type { LoginAudience, LoginResponse, SessionUser } from '@naar/shared';

let accessToken: string | null = null;
let currentUser: SessionUser | null = null;
let expiresAt = 0;

type Listener = (user: SessionUser | null) => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener(currentUser);
}

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getCurrentUser(): SessionUser | null {
  return currentUser;
}

export function setSession(response: LoginResponse): void {
  accessToken = response.accessToken;
  currentUser = response.user;
  /**
   * Se renueva 30 s ANTES de caducar. Sin ese margen, una petición lanzada justo en el límite
   * sale con un token que caduca en vuelo y falla con un 401 que el usuario no entiende.
   */
  expiresAt = Date.now() + (response.expiresInSeconds - 30) * 1000;
  notify();
}

export function clearSession(): void {
  accessToken = null;
  currentUser = null;
  expiresAt = 0;
  notify();
}

export function isAccessTokenExpired(): boolean {
  return accessToken === null || Date.now() >= expiresAt;
}

/**
 * Renueva el access token usando la cookie de refresco.
 *
 * La promesa se comparte entre llamadas concurrentes. Es imprescindible: si cinco consultas
 * de TanStack Query reciben un 401 a la vez y cada una renueva por su cuenta, la ROTACIÓN de
 * refresh tokens detecta cuatro reutilizaciones y revoca la familia entera — cerrando la
 * sesión del usuario por hacer las cosas bien.
 */
let refreshInFlight: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        // Sin esto el navegador no envía la cookie y el refresco falla siempre.
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        clearSession();
        return false;
      }

      setSession((await response.json()) as LoginResponse);
      return true;
    } catch {
      clearSession();
      return false;
    } finally {
      // Se libera en el siguiente tick para que las llamadas que ya estaban esperando
      // compartan ESTA promesa y no arranquen una segunda ronda.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

export async function login(
  email: string,
  password: string,
  audience?: LoginAudience,
): Promise<LoginResponse> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password, ...(audience ? { audience } : {}) }),
  });

  /*
   * El cuerpo se lee como TEXTO y se interpreta después. Cuando la API no responde —recién
   * recreado su contenedor, todavía migrando, o caída— el proxy de Vite (o nginx) devuelve un
   * 502/504 SIN cuerpo, y `response.json()` reventaba con «Failed to execute 'json' on
   * 'Response': Unexpected end of JSON input», que es lo que acababa en la pantalla de acceso.
   */
  const text = await response.text();
  let body: LoginResponse | { error?: { code: string; message: string; details?: unknown } } | null = null;
  try {
    body = text ? (JSON.parse(text) as typeof body) : null;
  } catch {
    body = null;
  }

  if (!body || !response.ok) {
    if (!body && (response.status === 0 || response.status >= 500)) {
      throw new SessionError(
        'API_UNAVAILABLE',
        'No se puede conectar con el servidor. Si acabas de arrancarlo, espera unos segundos y vuelve a probar.',
      );
    }
    const error = (body as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    throw new SessionError(
      error?.code ?? 'LOGIN_FAILED',
      error?.message ?? 'No se ha podido iniciar sesión.',
      error?.details,
    );
  }

  setSession(body as LoginResponse);
  return body as LoginResponse;
}

export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } finally {
    // Se limpia SIEMPRE, aunque la petición falle: si no, el usuario pulsa «salir», ve un
    // error y sigue dentro, que es lo peor que puede pasar en ese momento.
    clearSession();
  }
}

export class SessionError extends Error {
  readonly code: string;
  /**
   * Detalle estructurado del error. En `WRONG_AUDIENCE` trae `{ audience }` con la puerta
   * correcta, que es lo que permite a la pantalla ofrecer «Entrar por ahí» en vez de dejar al
   * usuario adivinando cuál de los dos botones era el suyo.
   */
  readonly details: unknown;

  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'SessionError';
    this.code = code;
    this.details = details;
  }
}
